import { useState, useEffect } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Tag } from "lucide-react";
import { useAuth } from "../../context/AuthContext";

export default function ManageCategories() {
  const [categories, setCategories] = useState([]);
  const [newName, setNewName] = useState("");
  const [newType, setNewType] = useState("expense");
  const [isCapex, setIsCapex] = useState(false);
  const { authAxios } = useAuth();

  useEffect(() => { fetchCategories(); }, []);
  const fetchCategories = async () => {
    try { const res = await authAxios.get("/categories"); setCategories(res.data); } catch (err) { console.error("Request failed:", err?.message || err); }
  };

  const handleAdd = async () => {
    if (!newName.trim()) return toast.error("Enter category name");
    try {
      await authAxios.post("/categories", { name: newName.trim(), type: newType, is_capex: isCapex });
      toast.success("Category added");
      setNewName(""); setIsCapex(false);
      fetchCategories();
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };

  const handleDelete = async (id) => {
    try { await authAxios.delete(`/categories/${id}`); toast.success("Deleted"); fetchCategories(); }
    catch { toast.error("Error"); }
  };

  const revenueCats = categories.filter(c => c.type === "revenue");
  const expenseCats = categories.filter(c => c.type === "expense");

  return (
    <div data-testid="manage-categories">
      {/* Add new */}
      <div className="bg-white rounded-2xl p-4 shadow-sm mb-4">
        <h3 className="font-bold text-sm text-[#1e3a5f] mb-3">Add Category</h3>
        <div className="flex gap-2 mb-2">
          <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Category name" className="flex-1 px-3 py-2 rounded-xl border text-sm" onKeyDown={e => e.key === "Enter" && handleAdd()} />
          <select value={newType} onChange={e => { setNewType(e.target.value); if (e.target.value === "revenue") setIsCapex(false); }} className="px-3 py-2 rounded-xl border text-sm">
            <option value="revenue">Revenue</option>
            <option value="expense">Expense</option>
          </select>
          <button onClick={handleAdd} className="px-4 py-2 rounded-xl bg-[#1e3a5f] text-white text-sm font-bold"><Plus className="w-4 h-4" /></button>
        </div>
        {newType === "expense" && (
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={isCapex} onChange={e => setIsCapex(e.target.checked)} className="accent-[#1e3a5f]" />
            CAPEX (assets — excluded from P&L)
          </label>
        )}
      </div>

      {/* Revenue categories */}
      <div className="mb-4">
        <h3 className="font-bold text-sm text-green-700 mb-2">Revenue Categories ({revenueCats.length})</h3>
        <div className="space-y-1">
          {revenueCats.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-green-50 rounded-xl px-3 py-2">
              <div className="flex items-center gap-2"><Tag className="w-3 h-3 text-green-600" /><span className="text-sm">{c.name}</span></div>
              <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {revenueCats.length === 0 && <p className="text-slate-400 text-xs">No revenue categories</p>}
        </div>
      </div>

      {/* Expense categories */}
      <div>
        <h3 className="font-bold text-sm text-red-600 mb-2">Expense Categories ({expenseCats.length})</h3>
        <div className="space-y-1">
          {expenseCats.map(c => (
            <div key={c.id} className="flex items-center justify-between bg-red-50 rounded-xl px-3 py-2">
              <div className="flex items-center gap-2">
                <Tag className="w-3 h-3 text-red-500" />
                <span className="text-sm">{c.name}</span>
                {c.is_capex && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">CAPEX</span>}
              </div>
              <button onClick={() => handleDelete(c.id)} className="text-red-400 hover:text-red-600"><Trash2 className="w-3 h-3" /></button>
            </div>
          ))}
          {expenseCats.length === 0 && <p className="text-slate-400 text-xs">No expense categories</p>}
        </div>
      </div>
    </div>
  );
}
